import type { HouseAdType } from "@yjsgames/house-ads";
import type { AdFormat } from "@yjsgames/ad-ports";

/**
 * 전체화면 하우스 광고 오버레이(HouseAdOverlay)의 DOM 미디어 생성/제어 순수부 (Phaser 비의존).
 * 엘리먼트 생성·볼륨 토글·닫기 지연 계산을 분리해 jsdom 단위테스트로 검증한다(`houseAdMedia.dom.test.ts`).
 * 레이아웃(전체화면 위치/크기)은 HouseAdOverlay가 적용한다.
 */

/**
 * adType별 DOM 미디어 엘리먼트 생성 (전체화면 오버레이용).
 * - `youtube`: `<iframe>` embed(autoplay+mute+enablejsapi는 URL 파라미터).
 * - `video`: `<video muted autoplay loop playsinline>`.
 * - `image`: `<img>`.
 * 재생/소스 속성만 설정하고, 위치·크기·입력차단은 HouseAdOverlay가 적용한다.
 */
export function createAdMediaElement(
  adType: HouseAdType,
  url: string,
): HTMLVideoElement | HTMLIFrameElement | HTMLImageElement | null {
  if (typeof document === "undefined") return null;
  if (adType === "youtube") {
    const iframe = document.createElement("iframe");
    iframe.src = url;
    iframe.setAttribute("allow", "autoplay; encrypted-media; picture-in-picture");
    iframe.setAttribute("referrerpolicy", "strict-origin-when-cross-origin");
    iframe.allowFullscreen = true;
    return iframe;
  }
  if (adType === "video") {
    const video = document.createElement("video");
    video.src = url;
    video.muted = true;
    video.autoplay = true;
    video.loop = true;
    video.setAttribute("playsinline", "");
    return video;
  }
  if (adType === "image") {
    const img = document.createElement("img");
    img.src = url;
    return img;
  }
  return null;
}

/**
 * 미디어 음소거 토글.
 * - video: `muted` 프로퍼티 직접 제어.
 * - youtube iframe: IFrame Player API postMessage(`mute`/`unMute`) — embed URL에 `enablejsapi=1` 필요.
 * - image: 사운드 없음 (no-op).
 */
export function setMediaMuted(
  el: HTMLVideoElement | HTMLIFrameElement | HTMLImageElement | null,
  muted: boolean,
): void {
  if (!el) return;
  if (el instanceof HTMLVideoElement) {
    el.muted = muted;
    return;
  }
  if (el instanceof HTMLIFrameElement) {
    const func = muted ? "mute" : "unMute";
    el.contentWindow?.postMessage(
      JSON.stringify({ event: "command", func, args: [] }),
      "*",
    );
  }
}

/**
 * 미디어 볼륨 설정 (0~100). 음소거 해제 시 풀볼륨이 아니라 데이터 지정 볼륨으로 재생되게 한다.
 * - video: `volume` 프로퍼티(0~1로 환산).
 * - youtube iframe: IFrame Player API postMessage(`setVolume`, [0~100]) — embed URL에 `enablejsapi=1` 필요.
 * - image: 사운드 없음 (no-op).
 */
export function setMediaVolume(
  el: HTMLVideoElement | HTMLIFrameElement | HTMLImageElement | null,
  volume0to100: number,
): void {
  if (!el) return;
  const v = Math.max(0, Math.min(100, Math.round(volume0to100)));
  if (el instanceof HTMLVideoElement) {
    el.volume = v / 100;
    return;
  }
  if (el instanceof HTMLIFrameElement) {
    el.contentWindow?.postMessage(
      JSON.stringify({ event: "command", func: "setVolume", args: [v] }),
      "*",
    );
  }
}

/** 닫기 버튼 노출까지의 강제 시청 시간(초) — reward 10초 / interstitial 5초. */
export function closeDelaySeconds(format: AdFormat): number {
  return format === "reward" ? 10 : 5;
}

/** 미디어 형식이 사운드를 가지는지 — image는 볼륨 버튼을 숨긴다. */
export function mediaHasSound(adType: HouseAdType): boolean {
  return adType === "video" || adType === "youtube";
}
